import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  VisitorPass,
  CreateVisitorPassRequest,
  VisitorPassPublicView,
  MarkPassUsedRequest,
  Visita,
  CreateWalkInVisitaRequest,
  ListVisitasQuery,
} from '@condo/contracts';
import { ApiRoutes } from '@condo/contracts';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/query-keys';

interface VisitorPassListResponse {
  items: VisitorPass[];
}

/** GET all passes for a condominium (conserje / admin view). */
export function useVisitorPasses(condoId: string) {
  return useQuery({
    queryKey: queryKeys.visitas(condoId),
    queryFn: () =>
      api.get<VisitorPassListResponse>(ApiRoutes.visitas(condoId)),
    enabled: !!condoId,
  });
}

/** GET the authenticated resident's own passes. */
export function useMyVisitorPasses() {
  return useQuery({
    queryKey: queryKeys.misVisitas(),
    queryFn: () =>
      api.get<VisitorPassListResponse>(ApiRoutes.misVisitas),
  });
}

/** POST create a new visitor pass. Invalidates both condo and personal lists. */
export function useCreateVisitorPass(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateVisitorPassRequest) =>
      api.post<VisitorPass, CreateVisitorPassRequest>(
        ApiRoutes.visitas(condoId),
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.visitas(condoId) });
      void qc.invalidateQueries({ queryKey: queryKeys.misVisitas() });
    },
  });
}

/** GET public pass verification (no auth required). Enabled when token exists. */
export function useVerifyPass(token: string | undefined) {
  return useQuery({
    queryKey: queryKeys.visitaVerificar(token ?? ''),
    queryFn: () =>
      api.get<VisitorPassPublicView>(ApiRoutes.visitaVerificar(token!)),
    enabled: !!token,
  });
}

/** POST mark a pass as used. Invalidates the verify query for this token. */
export function useMarkPassUsed(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MarkPassUsedRequest) =>
      api.post<void, MarkPassUsedRequest>(
        ApiRoutes.visitaMarcarUsada(token),
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.visitaVerificar(token),
      });
    },
  });
}

interface VisitaListResponse {
  items: Visita[];
}

/** GET /visitas/ingresos — list visits (admin/conserje). */
export function useVisitasIngresos(
  condoId: string,
  query: ListVisitasQuery = {},
) {
  const queryString = new URLSearchParams(
    Object.entries(query).filter(([, v]) => v !== undefined) as [string, string][],
  ).toString();
  const url = queryString
    ? `${ApiRoutes.visitasIngresosList(condoId)}?${queryString}`
    : ApiRoutes.visitasIngresosList(condoId);
  return useQuery({
    queryKey: ['visitas', 'ingresos', condoId, query],
    queryFn: () => api.get<VisitaListResponse>(url),
    enabled: !!condoId,
  });
}

/** POST /visitas/walk-in — conserje/admin registers a walk-in visit. */
export function useRegistrarWalkIn(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWalkInVisitaRequest) =>
      api.post<Visita, CreateWalkInVisitaRequest>(
        ApiRoutes.visitasWalkIn(condoId),
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['visitas', 'ingresos', condoId] });
    },
  });
}

/** POST /visitas/ingresos/{id}/egreso — mark visit as finalizada. */
export function useRegistrarEgreso(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<Visita, undefined>(ApiRoutes.visitasEgreso(condoId, id), undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['visitas', 'ingresos', condoId] });
    },
  });
}

/** GET /unidades/{unidadId}/visitas — historial for a unit. */
export function useHistorialUnidad(condoId: string, unidadId: string) {
  return useQuery({
    queryKey: ['visitas', 'historial-unidad', condoId, unidadId],
    queryFn: () =>
      api.get<VisitaListResponse>(
        ApiRoutes.visitasHistorialUnidad(condoId, unidadId),
      ),
    enabled: !!condoId && !!unidadId,
  });
}
